import React from 'react';
import { motion } from 'motion/react';
import { Star, Share2, MapPin, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { UserProfile } from '../../types';

interface BarberCardProps {
  barber: UserProfile;
  onClick: () => void;
  onNotify: (notif: { message: string, type: 'success' | 'info' }) => void;
}

export const BarberCard: React.FC<BarberCardProps> = ({ barber, onClick, onNotify }) => {
  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareData = {
      title: `CukurJe - ${barber.name}`,
      text: `Pesan layanan kapster profesional dari ${barber.name} di CukurJe!`,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData).catch(() => {
        copyToClipboard();
      });
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    const url = window.location.href;
    
    const fallbackCopy = () => {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        onNotify({ message: 'Link profil disalin ke clipboard!', type: 'success' });
      } catch (err) {
        onNotify({ message: 'Gagal menyalin link.', type: 'error' as any });
      }
      document.body.removeChild(textArea);
    };

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        onNotify({ message: 'Link profil disalin ke clipboard!', type: 'success' });
      }).catch(() => {
        fallbackCopy();
      });
    } else {
      fallbackCopy();
    }
  };

  return (
    <motion.div 
      whileHover={{ y: -8 }}
      transition={{ type: 'spring', stiffness: 300 }}
      className={cn(
        "bg-white/80 backdrop-blur-md rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)] border-[4px] group cursor-pointer",
        barber.gender === 'wanita' ? "border-pink-500" : "border-emerald-500"
      )}
      onClick={onClick}
    >
      <div className="relative h-36 overflow-hidden">
        <img src={barber.avatar} alt={barber.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
        <div className="absolute top-2 right-2 flex flex-col gap-1.5">
          <div className="bg-white/90 backdrop-blur-md px-2 py-0.5 rounded-lg flex items-center gap-1 shadow-sm">
            <Star className="w-2.5 h-2.5 text-emerald-500 fill-emerald-500" />
            <span className="text-[9px] font-bold text-slate-900">{barber.ratingCount > 0 ? barber.rating : 'Baru'}</span>
            {barber.ratingCount > 0 && <span className="text-[7px] text-slate-400">({barber.ratingCount})</span>}
          </div>
          <button 
            onClick={handleShare}
            className="w-7 h-7 bg-white/90 backdrop-blur-md rounded-lg flex items-center justify-center shadow-sm hover:bg-emerald-500 hover:text-white transition-all active:scale-95"
          >
            <Share2 className="w-3 h-3" />
          </button>
        </div>
        <div className="absolute bottom-2 left-2">
          <span className={cn(
            "text-white text-[8px] uppercase font-bold px-1.5 py-0.5 rounded-full shadow-lg transition-all",
            barber.status === 'Sibuk' ? "bg-amber-500 shadow-amber-500/40" :
            barber.status === 'Non-Aktif' ? "bg-slate-500 shadow-slate-500/40" :
            "bg-emerald-500 shadow-emerald-500/40"
          )}>
            {barber.status || 'Tersedia'}
          </span>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between mb-0.5 gap-2">
          <h3 className="text-sm font-bold text-slate-900 truncate">{barber.name}</h3>
          {(barber.price > 0 || (barber.services && barber.services.length > 0)) && (
            <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md flex-shrink-0">
              Rp {(barber.services && barber.services.length > 0) 
                ? Math.min(...barber.services.map((s: any) => s.price)).toLocaleString()
                : barber.price.toLocaleString()}
            </span>
          )}
        </div>
        <p className="text-slate-500 text-[10px] mb-1.5 line-clamp-1 leading-tight">
          {barber.bio || 'Kapster berpengalaman.'}
        </p>

        {(barber.city || barber.district) && (
          <div className="flex items-center gap-1 text-slate-400 text-[9px] font-bold mb-2">
            <MapPin className="w-2.5 h-2.5 text-slate-300" />
            <span className="truncate">{barber.city}{barber.district ? `, ${barber.district}` : ''}</span>
          </div>
        )}
        
        <div className="flex flex-wrap gap-1 mb-3">
          {(barber.specialties || ['Fade', 'Taper']).slice(0, 2).map(s => (
            <span key={s} className="bg-slate-50 text-slate-500 text-[8px] font-bold px-1.5 py-0.5 rounded-md border border-slate-100">
              {s}
            </span>
          ))}
        </div>

        <button className="w-full bg-slate-900 hover:bg-emerald-600 text-white py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 group-hover:shadow-lg group-hover:shadow-emerald-500/20 active:scale-95">
          Lihat & Booking
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
};
